import * as React from "react";

/**
 * Box Elements asset loader utility
 * Ensures Box CSS and JS are loaded before Box components are rendered
 */

interface BoxAssetLoader {
  loadBoxAssets(): Promise<void>;
  isBoxLoaded(): boolean;
}

const BOX_ELEMENTS_BASE =
  "https://cdn01.boxcdn.net/platform/elements/19.0.0/en-US";

type BoxGlobal = Record<string, unknown> | undefined;
const boxGlobal = () => (window as unknown as { Box?: BoxGlobal }).Box;

/**
 * Each Box Elements bundle defines its constructors on `window.Box`. Every
 * member any bundle has defined is remembered here, and after each bundle loads
 * whatever the current `window.Box` lacks is put back onto it, in place. This
 * holds whatever order the explorer, picker and preview (SOPs) bundles finish
 * in, including when their loads overlap.
 */
const knownBoxMembers: Record<string, unknown> = {};

const keepAllBoxMembers = () => {
  const current = boxGlobal();
  if (!current) return;
  Object.keys(knownBoxMembers).forEach((key) => {
    if (!(key in current)) current[key] = knownBoxMembers[key];
  });
  Object.assign(knownBoxMembers, current);
};

/** The explorer and picker bundles are loaded once both constructors exist. */
const hasExplorerAndPickers = () =>
  Boolean(boxGlobal()?.ContentExplorer && boxGlobal()?.FolderPicker);

class BoxAssetLoaderImpl implements BoxAssetLoader {
  private loadPromise: Promise<void> | null = null;
  private isLoaded = false;

  loadBoxAssets(): Promise<void> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this.loadAssets();
    return this.loadPromise;
  }

  isBoxLoaded(): boolean {
    return (
      this.isLoaded && typeof window !== "undefined" && hasExplorerAndPickers()
    );
  }

  private async loadAssets(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded. Another bundle (the SOPs preview) may have
      // created `window.Box` already, so look for these bundles' own constructors.
      if (typeof window !== "undefined" && hasExplorerAndPickers()) {
        this.isLoaded = true;
        resolve();
        return;
      }

      const totalAssets = 4; // 2 CSS + 2 JS
      let loadedAssets = 0;

      keepAllBoxMembers();
      const checkComplete = () => {
        loadedAssets++;
        keepAllBoxMembers();
        if (loadedAssets === totalAssets) {
          this.isLoaded = true;
          resolve();
        }
      };

      const handleError = (error: any) => {
        reject(new Error(`Failed to load Box assets: ${error}`));
      };

      // Load CSS files
      const explorerCSS = document.createElement("link");
      explorerCSS.rel = "stylesheet";
      explorerCSS.href =
        "https://cdn01.boxcdn.net/platform/elements/19.0.0/en-US/explorer.css";
      explorerCSS.crossOrigin = "anonymous";
      explorerCSS.onload = checkComplete;
      explorerCSS.onerror = handleError;
      document.head.appendChild(explorerCSS);

      const pickerCSS = document.createElement("link");
      pickerCSS.rel = "stylesheet";
      pickerCSS.href =
        "https://cdn01.boxcdn.net/platform/elements/19.0.0/en-US/picker.css";
      pickerCSS.crossOrigin = "anonymous";
      pickerCSS.onload = checkComplete;
      pickerCSS.onerror = handleError;
      document.head.appendChild(pickerCSS);

      // Load JS files
      const explorerJS = document.createElement("script");
      explorerJS.src =
        "https://cdn01.boxcdn.net/platform/elements/19.0.0/en-US/explorer.js";
      explorerJS.crossOrigin = "anonymous";
      explorerJS.onload = checkComplete;
      explorerJS.onerror = handleError;
      document.body.appendChild(explorerJS);

      const pickerJS = document.createElement("script");
      pickerJS.src =
        "https://cdn01.boxcdn.net/platform/elements/19.0.0/en-US/picker.js";
      pickerJS.crossOrigin = "anonymous";
      pickerJS.onload = checkComplete;
      pickerJS.onerror = handleError;
      document.body.appendChild(pickerJS);
    });
  }
}

// Export singleton instance
export const boxAssetLoader = new BoxAssetLoaderImpl();

let previewAssetsPromise: Promise<void> | null = null;

/**
 * Box's ContentPreview ships in its own bundle (the SOPs page shows uploaded
 * files with it). Loaded once; it keeps whatever the explorer and picker
 * bundles already defined on `window.Box`.
 */
export function loadBoxPreviewAssets(): Promise<void> {
  if (boxGlobal()?.ContentPreview) return Promise.resolve();
  if (previewAssetsPromise) return previewAssetsPromise;
  previewAssetsPromise = new Promise((resolve, reject) => {
    keepAllBoxMembers();
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = `${BOX_ELEMENTS_BASE}/preview.css`;
    document.head.appendChild(css);
    const script = document.createElement("script");
    script.src = `${BOX_ELEMENTS_BASE}/preview.js`;
    script.onload = () => {
      keepAllBoxMembers();
      resolve();
    };
    script.onerror = () => {
      previewAssetsPromise = null;
      reject(new Error("Box preview failed to load"));
    };
    document.body.appendChild(script);
  });
  return previewAssetsPromise;
}

// React hook for Box components
export function useBoxAssets() {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (boxAssetLoader.isBoxLoaded()) {
      setIsLoaded(true);
      return;
    }

    setIsLoading(true);
    boxAssetLoader
      .loadBoxAssets()
      .then(() => {
        setIsLoaded(true);
        setError(null);
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  return { isLoaded, isLoading, error };
}
